import React, { useMemo } from 'react';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { useThemeStore } from '../../stores';
import { getChartPalette } from './chartTheme';
import type { InsightsDevice } from '../../types';

interface DeviceSplitProps {
  devices: InsightsDevice[];
}

/**
 * Device mix as a proportion bar plus a directly-labelled breakdown.
 * Devices are entities, so each keeps a fixed palette slot regardless of rank;
 * anything beyond the three known form factors folds into a neutral "Other".
 */
const DEVICE_META: Record<string, { label: string; slot: number | 'other'; Icon: typeof ComputerDesktopIcon }> = {
  desktop: { label: 'Desktop', slot: 0, Icon: ComputerDesktopIcon },
  mobile: { label: 'Mobile', slot: 1, Icon: DevicePhoneMobileIcon },
  tablet: { label: 'Tablet', slot: 2, Icon: DeviceTabletIcon },
};

export const DeviceSplit: React.FC<DeviceSplitProps> = ({ devices }) => {
  const { theme } = useThemeStore();
  const colors = getChartPalette(theme === 'dark');

  const rows = useMemo(() => {
    const total = devices.reduce((acc, device) => acc + device.sessions, 0);

    return {
      total,
      items: devices.map((device) => {
        const meta = DEVICE_META[device.device] || {
          label: 'Other',
          slot: 'other' as const,
          Icon: QuestionMarkCircleIcon,
        };
        return {
          ...device,
          label: meta.label,
          Icon: meta.Icon,
          color: meta.slot === 'other' ? colors.other : colors.series[meta.slot],
          share: total > 0 ? Math.round((device.sessions / total) * 1000) / 10 : 0,
        };
      }),
    };
  }, [devices, colors]);

  if (rows.total === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-secondary-500 dark:text-secondary-400">
        No device data for this period.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 2px surface gaps keep adjacent segments legible without borders */}
      <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full">
        {rows.items.map((item) => (
          <div
            key={item.device}
            className="h-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${item.share}%`, backgroundColor: item.color }}
            title={`${item.label}: ${item.share}%`}
          />
        ))}
      </div>

      <div className="space-y-2.5">
        {rows.items.map((item) => (
          <div key={item.device} className="flex items-center gap-3">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: `${item.color}1a` }}
            >
              <item.Icon className="h-4 w-4" style={{ color: item.color }} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{item.label}</p>
              <p className="text-xs text-secondary-500 dark:text-secondary-400 tabular-nums">
                {item.sessions.toLocaleString()} sessions · {item.conversionRate}% convert
              </p>
            </div>
            <span className="text-sm font-semibold tabular-nums text-secondary-900 dark:text-secondary-50">
              {item.share}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeviceSplit;
